/** NCR overview. Mounted at /quality/ncrs by the quality navigation owner. */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { EM_DASH } from "@/lib/utils";

import {
  fetchQualityNcrs,
  type QualityNcrDisposition,
  type QualityNcrListSort,
  type QualityNcrStatus,
} from "./qualityEventNcrApi";

const HELP_TEXT =
  "Every NCR is one quality incident. This list shows the number, status, and disposition. Open a row for the unit, the resolution, and the notes.";

const PAGE_SIZE = 100;

const STATUS_OPTIONS: Array<{ value: QualityNcrStatus; label: string }> = [
  { value: "unresolved", label: "Unresolved" },
  { value: "potting_rework", label: "Potting rework" },
  { value: "rework_failed", label: "Rework failed" },
  { value: "resolved", label: "Resolved" },
];

const DISPOSITION_OPTIONS: Array<{ value: QualityNcrDisposition; label: string }> = [
  { value: "repair", label: "Repair" },
  { value: "scrap", label: "Scrap" },
  { value: "touch_sample", label: "Touch sample" },
];

function statusLabel(status: QualityNcrStatus): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function statusTone(status: QualityNcrStatus): BadgeTone {
  if (status === "resolved") return "success";
  if (status === "rework_failed") return "warning";
  if (status === "potting_rework") return "info";
  return "neutral";
}

function dispositionLabel(disposition: QualityNcrDisposition | null): string {
  if (disposition == null) return EM_DASH;
  return DISPOSITION_OPTIONS.find((option) => option.value === disposition)?.label ?? disposition;
}

export function QualityNcrsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statuses, setStatuses] = useState<QualityNcrStatus[]>([]);
  const [dispositions, setDispositions] = useState<QualityNcrDisposition[]>([]);
  const [sortKey, setSortKey] = useState<QualityNcrListSort>("id");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, statuses, dispositions, sortKey, sortDir]);

  const listQuery = useQuery({
    queryKey: ["quality", "ncrs", debouncedSearch, statuses, dispositions, sortKey, sortDir, offset],
    queryFn: () =>
      fetchQualityNcrs({
        search: debouncedSearch || undefined,
        status: statuses.length > 0 ? statuses : undefined,
        disposition: dispositions.length > 0 ? dispositions : undefined,
        sort: sortKey,
        direction: sortDir,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const rows = listQuery.data?.rows ?? [];
  const totalCount = listQuery.data?.totalCount ?? 0;
  const filtersActive = debouncedSearch !== "" || statuses.length > 0 || dispositions.length > 0;
  const pageStart = totalCount === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + rows.length, totalCount);

  const toggleSort = (key: QualityNcrListSort) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "id" ? "desc" : "asc");
  };

  const toggleStatus = (value: QualityNcrStatus) => {
    setStatuses((current) =>
      current.includes(value) ? current.filter((status) => status !== value) : [...current, value],
    );
  };

  const toggleDisposition = (value: QualityNcrDisposition) => {
    setDispositions((current) =>
      current.includes(value)
        ? current.filter((disposition) => disposition !== value)
        : [...current, value],
    );
  };

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint={HELP_TEXT}>NCRs</PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="quality-ncr-search">Search NCRs</Label>
            <Input
              id="quality-ncr-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by NCR number"
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((option) => {
                const pressed = statuses.includes(option.value);
                return (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={pressed ? "primary" : "secondary"}
                    aria-pressed={pressed}
                    onClick={() => toggleStatus(option.value)}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Disposition</p>
            <div className="flex flex-wrap gap-2">
              {DISPOSITION_OPTIONS.map((option) => {
                const pressed = dispositions.includes(option.value);
                return (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={pressed ? "primary" : "secondary"}
                    aria-pressed={pressed}
                    onClick={() => toggleDisposition(option.value)}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
          {filtersActive ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setSearch("");
                setStatuses([]);
                setDispositions([]);
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </CardBody>
      </Card>

      <HubViewportCard>
        <CardHeader>
          <CardTitle>
            {listQuery.isLoading
              ? "Loading NCRs…"
              : filtersActive
                ? `Showing ${pageStart.toLocaleString()}-${pageEnd.toLocaleString()} of ${totalCount.toLocaleString()}`
                : `${totalCount.toLocaleString()} NCR${totalCount === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardBody className="min-h-0 overflow-auto p-0">
          {listQuery.isError ? (
            <p className="p-4 text-sm text-red-700">{(listQuery.error as Error).message}</p>
          ) : listQuery.isLoading ? null : rows.length === 0 ? (
            <p className="p-4 text-sm text-slate-600">
              {filtersActive ? "No NCRs match these filters." : "No NCRs yet."}
            </p>
          ) : (
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50 shadow-[0_1px_0_0_rgb(226_232_240)]">
                <tr>
                  <SortableTableHeader
                    label="Number"
                    sortKey="id"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="sticky top-0 z-20"
                  />
                  <SortableTableHeader
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="sticky top-0 z-20"
                  />
                  <SortableTableHeader
                    label="Disposition"
                    sortKey="disposition"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="sticky top-0 z-20"
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-b border-slate-100 hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80"
                    onClick={() => navigate(`/quality/ncrs/${row.id}`)}
                  >
                    <td className="px-2 py-3 font-medium tabular-nums">
                      <Link
                        className="text-sky-700 hover:underline"
                        to={`/quality/ncrs/${row.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {row.id}
                      </Link>
                    </td>
                    <td className="px-2 py-3">
                      <Badge tone={statusTone(row.status)}>{statusLabel(row.status)}</Badge>
                    </td>
                    <td className="px-2 py-3 text-slate-800">{dispositionLabel(row.disposition)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
        {totalCount > PAGE_SIZE ? (
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={offset === 0 || listQuery.isFetching}
              onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={offset + PAGE_SIZE >= totalCount || listQuery.isFetching}
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </HubViewportCard>
    </div>
  );
}
